
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDate } from "@/lib/blogUtils";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { BlogPost } from "@/types/blog";
import { sanitizeHtml, decodeHtmlEntities } from "@/utils/htmlUtils";

export interface BlogCardProps {
  id?: string;
  title?: string;
  featuredImage?: string;
  excerpt?: string;
  date?: string;
  categories?: string[];
  url?: string;
  compact?: boolean;
  className?: string;
  post?: BlogPost; // Add post prop for passing the entire BlogPost object
}

export function BlogCard({ 
  id, 
  title, 
  featuredImage, 
  excerpt, 
  date, 
  categories, 
  url,
  compact = false,
  className,
  post
}: BlogCardProps) {
  // If post object is provided, use its properties
  const postId = post?.id || id || "";
  const rawTitle = post?.title || title || "";
  const postImage = post?.featuredImage || featuredImage || "";
  const rawExcerpt = post?.excerpt || excerpt || "";
  const postDate = post?.date || date || "";
  const postCategories = post?.categories || categories || [];
  const postUrl = post?.url || url || `/blog/${post?.slug || postId}`;
  
  // Process title to decode HTML entities
  const postTitle = decodeHtmlEntities(rawTitle);
  
  // Process excerpt to remove HTML tags and decode entities
  const postExcerpt = decodeHtmlEntities(rawExcerpt.replace(/<[^>]+>/g, ''));
  
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [optimizedImageUrl, setOptimizedImageUrl] = useState("");
  
  // Format card date
  const formattedDate = formatDate(postDate);
  
  // Get primary category (first one)
  const primaryCategory = postCategories && postCategories.length > 0 ? postCategories[0] : null;
  
  // Set initial state during first render
  useEffect(() => {
    if (!postImage) {
      setImageError(true);
      setImageLoading(false);
      return;
    }
    
    setImageError(false);
  }, [postImage]);
  
  return (
    <Card 
      className={cn(
        "overflow-hidden h-full flex flex-col transition-all duration-300 hover:shadow-md group border-metal-light/20",
        compact ? "max-w-sm" : "max-w-none",
        className
      )}
    >
      <Link to={postUrl} className="flex-grow flex flex-col h-full">
        <div className={cn(
          "relative overflow-hidden",
          compact ? "h-40" : "h-48"
        )}>
          {/* Always show skeleton during loading */}
          {imageLoading && (
            <Skeleton className="absolute inset-0" />
          )}
          
          {/* Only attempt to load image if we have a URL and no error */}
          {postImage && !imageError && (
            <OptimizedImage
              src={postImage}
              alt={postTitle}
              width={compact ? 400 : 600}
              height={compact ? 225 : 337} // 16:9 aspect ratio
              className="group-hover:scale-105 transition-transform duration-500"
              onLoad={() => setImageLoading(false)}
              onError={() => {
                console.error("[BlogCard] Failed to load image:", postImage);
                setImageError(true);
                setImageLoading(false);
              }}
            />
          )}
          
          {/* Show fallback when image fails to load or doesn't exist */}
          {imageError && (
            <div className="w-full h-full flex items-center justify-center bg-muted">
              <span className="text-muted-foreground text-sm">Immagine non disponibile</span>
            </div>
          )}
          
          {primaryCategory && (
            <div className="absolute bottom-2 left-2">
              <Badge variant="secondary" className="bg-white/80 text-primary hover:bg-white/90">
                {primaryCategory}
              </Badge>
            </div>
          )}
        </div>
        
        <CardContent className="flex-grow flex flex-col p-4">
          <div className="text-sm text-muted-foreground mb-2">
            {formattedDate}
          </div>
          
          <h3 className="text-lg md:text-xl font-semibold mb-2 line-clamp-2 group-hover:text-primary transition-colors">
            {postTitle}
          </h3>
          
          {!compact && (
            <p className="text-muted-foreground line-clamp-3 text-sm">
              {postExcerpt}
            </p>
          )}
        </CardContent>
      </Link>
    </Card>
  );
}
